import type { Component } from 'vue'
import SowConfirmationModal from './SowConfirmationModal.vue'
import CancelHireComplianceModal from './CancelHireComplianceModal.vue'
import { ComplianceName } from '@/gql/graphql'
import ContractDocComplianceModal from './ContractDocComplianceModal.vue'

export interface ComplianceModalProps {
  open?: boolean
  hireId: string
  hideFooter?: boolean
}

/**
 * Static registry of compliance modals.
 *
 * New compliances should declare an `inputIdentifier` on the compliance
 * class instead. The modal will be auto-discovered from this folder.
 */
export const complianceModals: Partial<Record<ComplianceName, Component<ComplianceModalProps>>> = {
  [ComplianceName.SoWConfirmation]: SowConfirmationModal,
  [ComplianceName.ContractDoc]: ContractDocComplianceModal,
  [ComplianceName.BusinessEntityTypeValidity]: CancelHireComplianceModal,
}

const discoveredModals = import.meta.glob('./*Modal.vue', { eager: true }) as Record<
  string,
  { default: Component<ComplianceModalProps> }
>

function resolveByIdentifier(
  identifier: string | null | undefined,
): Component<ComplianceModalProps> | null {
  if (!identifier) return null
  return discoveredModals[`./${identifier}.vue`]?.default ?? null
}

/**
 * Check if a modal exists for the given compliance — either in the
 * static registry or via the compliance's input identifier.
 */
export function hasComplianceModal(
  complianceName: ComplianceName,
  inputIdentifier?: string | null,
): boolean {
  return complianceName in complianceModals || resolveByIdentifier(inputIdentifier) !== null
}

/**
 * Get the modal component. Checks the static registry first, then
 * falls back to resolving by the compliance's input identifier.
 */
export function getComplianceModal(
  complianceName: ComplianceName,
  inputIdentifier?: string | null,
): Component<ComplianceModalProps> | null {
  return complianceModals[complianceName] ?? resolveByIdentifier(inputIdentifier)
}
